import {css} from '@emotion/css';
import {createSignal, Show} from 'solid-js';
import * as ImageSizes from 'Shared/model/ImageSizes';
import Assert from 'Common/Assert';
import { uploadResponseJson } from 'Browser/UploadJson';
import {ImageSchema, ImageSize, Url} from 'Shared/model/basic';
import { ImageCropper } from 'Shared/forms/ImageCropper';
import { noteStyle } from 'Shared/forms/Wrap';
import { sharedTheme as theme } from 'Shared/ThemeProvider';
import { useUrls } from 'Shared/UrlProvider';
import { useServer } from 'Shared/SiteProvider';
import { ImagePreview } from 'Shared/forms/ImagePreview';
import { ImageUploadDragAndDrop } from 'Shared/forms/ImageUploadDragAndDrop';
import { useStore } from 'Shared/StoreProvider';
import { storeData } from 'Common/ViewUtils';
import { reconcile } from 'solid-js/store';
import { describeMegabytes, maxUploadBytes } from 'Common/config/ImageTypes';


/* avif is accepted even though it is never stored as such - the server converts it to webp. */
export const supportedImageFileTypes = ['image/jpeg','image/png','image/webp','image/avif'];

export interface IImageUpload {
	folder: string;
	image: ImageSchema,
	previewSize:ImageSize,
	small?: boolean,

	fallbackUrl?: Url;
	previewRatio: number;

	/* Width in px of the preview and its placeholder. Left out, they keep their default sizes. */
	previewWidth?: number;

	/* Note cropping will happen if dimensions.ratio exists */
	dimensions: keyof typeof ImageSizes;

	/* Force cropping on/off. When undefined cropping happens iff dimensions.ratio exists.
	   Setting it true on a ratio-less dimensions gives free-form (no fixed aspect) cropping. */
	crop?: boolean;

	/* Called after an image has been successfully uploaded (and after any cropping). */
	onUploaded?: () => void;

	/* Called after the image has been removed. */
	onRemoved?: () => void;

	/* Store/read the asset under __SHARED rather than the current site key (platform-wide
	   shared assets, e.g. a shared poster theme's images). */
	shared?: boolean;
}

/*
	Options include 'ratio', 'minWidth' and 'minHeight' which are all optional. 
	If 'ratio' is supplied only one of either 'minWidth' and SminHeight' are required to 
	enforce minimum dimensions.

	TODO re cropping... in the past I didn't require cropping for venue logos, parallax images, etc - and it 
	     may be better to allow this again in future - leaving it to the user to tweak the images.
		 In this case any dimensions supplied would need to do without 'ratio' I guess. Could potentially
		 still have multiple sizes supported.
*/
export function ImageUpload(P:IImageUpload)
{
	const largeStyle = css({
		div: {
			margin: '15px 0'
		},

		img: {
			maxWidth: 450,
			maxHeight: 300
		}
	});

	const smallStyle = css({
		cursor: 'pointer',

		div: {
			margin: '3px 0'
		},

		img: {
			maxWidth: 350,
			maxHeight: 200,
			width: '100%',
			height: 'auto'
		},

		button: {
			padding: '4px 9px'
		}
	});

	const [buttonsNode,setButtonsNode] = createSignal();

	return (
		<ImageUploadDragAndDrop dimensions={P.dimensions} folder={P.folder} crop={P.crop} shared={P.shared} onUploaded={P.onUploaded}>
			<div class={P.small ? smallStyle : largeStyle}>
				<ImageUploadButtons node={buttonsNode} setNode={setButtonsNode} dimensions={P.dimensions} folder={P.folder} showDropMessage={false} crop={P.crop} shared={P.shared} onUploaded={P.onUploaded} onRemoved={P.onRemoved} />

				<ImagePreview node={buttonsNode}
					setNode={setButtonsNode}
					folder={P.folder}
					size={P.previewSize}
					image={P.image}
					fallbackUrl={P.fallbackUrl}
					ratio={P.previewRatio}
					width={P.previewWidth}
					shared={P.shared}
				/>
			</div>
		</ImageUploadDragAndDrop>
	);
}

interface ICallCropper {
	dimensions: keyof typeof ImageSizes;
	file: Blob|MediaSource;
	setShowCrop;
	done: (blob:XXX)=>void;
}

export function CallCropper(P:ICallCropper)
{
	const {minWidth,minHeight,ratio} = calcImageDimensions(P.dimensions);
	const objectUrl = URL.createObjectURL(P.file);

	return (
		<ImageCropper url={objectUrl} mimeType={P.file.type} ratio={ratio} minWidth={minWidth} minHeight={minHeight} setShowCrop={P.setShowCrop} done={P.done}/>
	);
}

//TODO share more between these interfaces
interface IImageUploadButtons {
	folder: string;
	dimensions: keyof typeof ImageSizes;
	node: ()=>HTMLElement,
	setNode: (e:HTMLElement)=>void,
	showDropMessage?: boolean,
	fallbackUrl?: Url;
	crop?: boolean;
	shared?: boolean;

	/* When set, the 'Remove image' button calls this instead of the default delete (which only
	   clears the image value). Lets the caller remove the whole field/array item, including the
	   image asset cleanup. */
	onRemove?: () => void;

	/* Called after an image has been successfully uploaded (and after any cropping). */
	onUploaded?: () => void;

	/* Called after the default removal above has run. */
	onRemoved?: () => void;
}

export function ImageUploadButtons(P:IImageUploadButtons)
{
	const buttonsStyle = css({
		button: {
			marginRight: 5,
		},
	});

	const removeImageStyle = css(theme().button, {
		backgroundColor: 'rgb(255, 115, 141)',
		borderColor: 'rgb(255, 90, 120)',

		'[disabled]': {
			filter: 'none !important', 	//XXX overriding forms.less
			backgroundColor: '#EEE',
		},

		':not([disabled])': {
			backgroundColor: '#ff738d',
			borderColor: '#ff5a78'
		}
	});


	const crop = () => P.crop ?? (ImageSizes[P.dimensions].ratio != undefined);
	const server = useServer();
	const storeWrap = useStore();
	const urls = useUrls();
	const uploadUrl = () => urls.uploadImageUrl(storeWrap,P.folder,P.dimensions,P.shared);
	const imageExists = () => storeData(storeWrap.location)?.hash != undefined;
	const [croppingFile,setCroppingFile] = createSignal(false);

	//XXX type is (value:ImageSchema|undefined) => void,
	const setImage = value => storeWrap.setStore(...storeWrap.location,reconcile(value));

	const deleteImage = () => {
		if (!confirm('Are you sure you want do delete this image?'))
			return;

		if (P.onRemove) {
			P.onRemove();
			return;
		}

		server.deleteImage.mutate({page:storeWrap.page,permission:storeWrap.permission,folder:P.folder,id:storeWrap.docId,location:storeWrap.location,siteKey:P.shared ? '__SHARED' : undefined});
		storeWrap.setStore(...storeWrap.location,undefined);
		setImage(undefined);
		P.onRemoved?.();
	};

	const fileChanged = async () => {
		await handleFile(P.node().files,P.node(),crop(),setCroppingFile,setImage,uploadUrl());

		/* When cropping, the upload only happens once the user confirms the crop (see cropDone). */
		if (!crop())
			P.onUploaded?.();
	};

	const cropDone = async blob => {
		await uploadImage(blob,uploadUrl(),setImage,setCroppingFile,P.node());
		P.onUploaded?.();
	};


	return (
		<div class={buttonsStyle}>
			<Show when={P.showDropMessage ?? true}>
				<div class={noteStyle()}>
					Drop image here <i>or</i> click to upload.
				</div>
			</Show>

			<input type='file' ref={P.setNode} 
				accept='.jpg,.jpeg,.png,.webp,.avif' autocomplete='off' name='files[]' style={{display:'none'}}
				onChange={fileChanged}
			/>

			<button type='button' class={css(theme().button)} onClick={() => P.node().click()}>
				<i class='fa fa-upload'></i> {imageExists() ? 'Replace' : 'Upload' } image
			</button>

			<button type='button' class={removeImageStyle} disabled={!imageExists()} onClick={deleteImage}>
				<i class='fa fa-times'></i> Remove image
			</button>

			<Show when={croppingFile()}>
				<CallCropper dimensions={P.dimensions} file={P.node().files.item(0)} setShowCrop={setCroppingFile} done={cropDone} />
			</Show>
		</div>
	);
}

export async function handleFile(files,uploadButton:HTMLInputElement|null,crop:boolean,setCroppingFile,setImage,uploadUrl)
{
	if (files.length > 1) {
		alert('Only expected one file');
		return;
	}
	const file = files[0];
	if (!supportedImageFileTypes.includes(file.type)) {
		alert('Unsupported file type');
		return;
	}

	/* Checked here so the user gets told, rather than having the request rejected by multer. */
	if (file.size > maxUploadBytes) {
		alert(`That image is ${describeMegabytes(file.size)}, which is too large to upload. `+
			`The limit is ${describeMegabytes(maxUploadBytes)}.`);
		return;
	}

	if (crop)
		setCroppingFile(file);
	else
		await uploadImage(file,uploadUrl,setImage,setCroppingFile,uploadButton);
}

function calcImageDimensions(dimensions: keyof typeof ImageSizes)
{
	const dims = ImageSizes[dimensions];
	const ratio = dims.ratio;	// may be undefined → free-form (no fixed aspect) cropping
	let minWidth = dims.minWidth;
	let minHeight = dims.minHeight;

	if (minHeight==null && (ratio!=null && minWidth!=null))
		minHeight = Math.round(minWidth / ratio);
	if (minWidth==null && (ratio!=null && minHeight!=null))
		minWidth = Math.round(minHeight * ratio);
	if (minWidth==null) minWidth = 0;
	if (minHeight==null) minHeight = 0;

	return {minWidth:minWidth,minHeight:minHeight,ratio:ratio};
}

export async function uploadImage(fileOrBlob,uploadUrl:string,setImage,setShowCrop,uploadButton)
{
	if (fileOrBlob==null) return;
	const dataUrl = await fileOrBlobToDataUrl(fileOrBlob);
	const file = new File([fileOrBlob],'trimmedImage',{type:fileOrBlob.type});

	let loaded = false;
	
	setTimeout(() => {
		if (!loaded) 
			setImage({dataUrl:dataUrl});
	},500);

	const fieldData = await uploadFile(file,uploadUrl);
	loaded = true;
	setImage(fieldData);
	setShowCrop(false);

	/* 
		Chrome won't upload the same image twice in a row when the buttons are used (DnD works OK though).
		Reset the value so this isn't a problem.
	*/
	if (uploadButton!=null)
		uploadButton.value = '';
}

async function fileOrBlobToDataUrl(fileOrBlob:File|Blob):Promise<string>
{
	/*
		For simplicity I'm aways returning a string. Might be better to return an ArrayBuffer instead or
		else pass string|ArrayBuffer back.

		Note on FF for cropping fileOrBlob=blob & FileReader returns a string.
	 */
	//XXX when trimming getting a Blob

	return await new Promise((resolve,reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(fileOrBlob);
		reader.onloadend = () => {
			const out = reader.result instanceof ArrayBuffer ? reader.result.toString() : reader.result;
			resolve(Assert.toString(out));
		}
	});
}

async function uploadFile(file:File,uploadUrl:string)
{
	const formData = new FormData();
	formData.append('file',file);

	const ret = await fetch(uploadUrl,{method:'POST',body:formData});

	/* Multer rejects an oversized body before any of our own code runs, so the response is not
	   necessarily JSON. Read the status before trying to parse it. */
	if (ret.status==413) {
		alert('That image is too large to upload. Please try a smaller one.');
		throw new Error('File too large');
	}

	const body = await uploadResponseJson(ret,uploadUrl);

	if (body?.errorType!=null)
		throw new Error('Error on server');

//XXX    Could possibly move the image-related functions into this file
	return body;
}

